/**
 * Player の画面（/player・2026-10-07 console/one-account・設計 §3）。旗 oneAccount が on のときだけ App.tsx がつなぐ。
 *
 * 3 つのタブ（2026-10-08 console/account-pages・founder「プレイヤーダッシュボードは 1 枚で縦長は無理があるでしょ」）:
 *   Games（/player）= つないだゲーム / Profile（/player/profile）= メール・表示名 / Sign-in（/player/sign-in）= 入り方 + 一番下に削除。
 *   account のページ（サーバーの players/account/tabs.ts）と同じ分け方 = 旗を on にしたときにどちらで見ても同じ形。タブは別の URL（NavLink）。
 *
 * 中身は account のページ（<accountUrl>/account）と同じ: 表示名・つないだゲーム（外す）・ログインの方法（足す・外す）・アカウントの削除。
 * **鍵はアカウントの鍵（kas_）だけ**。呼ぶのは accountApi.ts の関数だけ（api() を直接呼ばない = 作り手の鍵が自動で付かない。
 *   oneAccount.test.ts が文字として確かめる）。
 * 入り方を足すのは account のページで行う（Core リード 2026-10-07: 乗っ取りに使える操作なので account のホストの Cookie と CSRF の内側だけ）。
 * 書き換えの口がまだ無いサーバー（route_missing）では「アカウントのページで変えて」とリンクを出す（Core が順に足す）。
 */
import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { ApiError } from '../api';
import {
  deleteAccount,
  disconnectGame,
  getAccountMe,
  hideGame,
  removeAccountMethod,
  renameAccount,
  startAccountSignIn,
  type AccountGame,
  type AccountMe,
  type AccountMethod
} from '../accountApi';
import { ModeSwitch } from '../components/ModeSwitch';
import { ConfirmDialog, LanguageSwitch, Logo, Spinner } from '../components/ui';
import { errorText, formatAgo, useT } from '../i18n';
import { accountPageUrl, PLAYER_PATH, PLAYER_PROFILE_PATH, PLAYER_SIGNIN_PATH, saveMode } from '../oneAccount';
import { PRODUCT_NAME } from '../productName';
import { useSession } from '../session';

/** 作り手のアカウントの設定（閉鎖 = AccountPage・App.tsx の /account）。Player の Delete account から作り手の人を案内する先 */
const CREATOR_ACCOUNT_PATH = '/account';

/** 最後に選んだ方（Player / Creator）を覚える入れ物。保存できない環境では null（覚えなくても切り替えはできる = oneAccount.ts saveMode） */
function safeLocal(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

/** ブランド名は訳さない。email だけ文言（メールのリンクかコード） */
const METHOD_NAMES: Record<Exclude<AccountMethod, 'email'>, string> = { google: 'Google', x: 'X', discord: 'Discord', apple: 'Apple', github: 'GitHub' };

/** 作り手でない人も使う上のバー（作り手の残高・ガイドなどは出さない = kas_ だけの画面に作り手の鍵の口を混ぜない） */
export function AccountTopBar({ children }: { children?: ReactNode }) {
  const { t } = useT();
  const { account, signOut } = useSession();
  return (
    <header className="topbar">
      <Link to={PLAYER_PATH} className="brand">
        <Logo size={26} />
        <span>{PRODUCT_NAME}</span>
      </Link>
      <div className="topbar-right">
        <ModeSwitch />
        {children}
        {account?.email && <span className="topbar-email muted">{account.email}</span>}
        <LanguageSwitch />
        <button type="button" className="btn btn-ghost btn-sm" onClick={() => void signOut()}>
          {t('common.logOut')}
        </button>
      </div>
    </header>
  );
}

type Confirm = { kind: 'game'; game: AccountGame } | { kind: 'method'; method: AccountMethod } | { kind: 'delete' } | null;

export type PlayerTab = 'games' | 'profile' | 'sign-in';

/** タブ。end = /player が /player/profile にいるときに「今いる」にならない（NavLink が aria-current="page" を付ける） */
function PlayerTabs() {
  const { t } = useT();
  const cls = ({ isActive }: { isActive: boolean }) => `tab${isActive ? ' is-active' : ''}`;
  return (
    <nav className="tabs player-tabs" aria-label={t('player.tabs.label')}>
      <NavLink to={PLAYER_PATH} end className={cls}>
        {t('player.tabs.games')}
      </NavLink>
      <NavLink to={PLAYER_PROFILE_PATH} end className={cls}>
        {t('player.tabs.profile')}
      </NavLink>
      <NavLink to={PLAYER_SIGNIN_PATH} end className={cls}>
        {t('player.tabs.signIn')}
      </NavLink>
    </nav>
  );
}

export function PlayerPage({ tab = 'games' }: { tab?: PlayerTab }) {
  const { t } = useT();
  const { account, oneAccount, dropAccount, updateAccount, signOut } = useSession();
  const [me, setMe] = useState<AccountMe | null>(null);
  const [loadError, setLoadError] = useState<ApiError | null>(null);
  const [name, setName] = useState('');
  const [note, setNote] = useState<{ tone: 'ok' | 'error' | 'elsewhere'; text: string } | null>(null);
  const [confirm, setConfirm] = useState<Confirm>(null);
  const [busy, setBusy] = useState(false);
  const token = account?.token ?? null;

  useEffect(() => {
    if (!token) return;
    let live = true;
    getAccountMe(token).then(
      (r) => {
        if (!live) return;
        setMe(r);
        setName(r.account.displayName ?? '');
      },
      (e: unknown) => {
        if (!live) return;
        // kas_ の 401 は api.ts のログアウトの合図にならない（auth:false）= ここで鍵を捨てて「もう一度ログイン」を出す
        if (e instanceof ApiError && e.status === 401) dropAccount();
        else setLoadError(e instanceof ApiError ? e : new ApiError(0, 'network_error', String(e)));
      }
    );
    return () => {
      live = false;
    };
  }, [token, dropAccount]);

  /** 失敗の出し分け: 口がまだ無い → アカウントのページへ・401 → 鍵を捨てる・ほか → サーバーの文 */
  const fail = (e: unknown) => {
    if (e instanceof ApiError && e.notAvailable) return setNote({ tone: 'elsewhere', text: t('player.notYetHere') });
    if (e instanceof ApiError && e.status === 401) return dropAccount();
    setNote({ tone: 'error', text: e instanceof ApiError ? errorText(e) : String(e) });
  };

  // 鍵が無い（作り手の鍵だけで入った・切れた）= もう一度 account で入る。戻ったらこの画面へ
  if (!token) {
    return (
      <div className="page-wrap">
        <AccountTopBar />
        <main className="container narrow" id="main">
          <h1>{t('player.title')}</h1>
          <p className="muted">{t('player.signInNeeded')}</p>
          {oneAccount.accountUrl && (
            <button type="button" className="btn btn-primary" onClick={() => void startAccountSignIn(oneAccount.accountUrl!, PLAYER_PATH)}>
              {t('oneAccount.callback.again')}
            </button>
          )}
        </main>
      </div>
    );
  }

  const saveName = async (e: FormEvent) => {
    e.preventDefault();
    setNote(null);
    setBusy(true);
    try {
      const r = await renameAccount(token, name.trim());
      updateAccount({ displayName: r.account.displayName });
      setMe((m) => (m ? { ...m, account: r.account } : m));
      setNote({ tone: 'ok', text: t('player.name.saved') });
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
    }
  };

  /** 一覧から隠す（サーバーは隠したものを /v1/account/me から除く = 手元の一覧からも外す） */
  const hide = async (g: AccountGame) => {
    setNote(null);
    setBusy(true);
    try {
      await hideGame(token, g.environmentId);
      setMe((m) => (m ? { ...m, games: m.games.filter((x) => x.environmentId !== g.environmentId) } : m));
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
    }
  };

  const runConfirm = async () => {
    if (!confirm) return;
    setNote(null);
    setBusy(true);
    try {
      if (confirm.kind === 'game' || confirm.kind === 'method') {
        if (confirm.kind === 'game') await disconnectGame(token, confirm.game.environmentId);
        else await removeAccountMethod(token, confirm.method);
        // 読み直す（メールの入り方を外すとメールの表示も変わる等、手元で合わせるより確か）
        setMe(await getAccountMe(token));
      } else {
        await deleteAccount(token);
        setConfirm(null);
        // 消えたアカウントの鍵は持っていても意味が無い = ログアウトと同じ後始末（作り手の鍵は 409 creator_account で先に断られる）
        await signOut();
        return;
      }
      setConfirm(null);
    } catch (err) {
      setConfirm(null);
      fail(err);
    } finally {
      setBusy(false);
    }
  };

  const methodName = (m: AccountMethod) => (m === 'email' ? t('player.methods.email') : METHOD_NAMES[m]);
  const accountPage = oneAccount.accountUrl ? accountPageUrl(oneAccount.accountUrl) : null;
  const accountPageLink = accountPage && (
    <a className="btn btn-sm" href={accountPage} target="_blank" rel="noopener noreferrer">
      {t('oneAccount.openAccountPage')}
    </a>
  );

  return (
    <div className="page-wrap">
      <AccountTopBar />
      <main className="container narrow" id="main">
        <h1>{t('player.title')}</h1>
        <PlayerTabs />
        {tab === 'games' && <p className="muted">{t('player.lead')}</p>}
        {note && (
          <p className={`alert ${note.tone === 'ok' ? 'alert-ok' : note.tone === 'error' ? 'alert-error' : 'alert-warn'}`} role={note.tone === 'ok' ? 'status' : 'alert'}>
            {note.text} {note.tone === 'elsewhere' && accountPageLink}
          </p>
        )}
        {loadError && (
          <p className="alert alert-error" role="alert">
            {errorText(loadError)}
          </p>
        )}
        {!me && !loadError && <Spinner />}
        {me && (
          <>
            {tab === 'profile' && (
              <section className="card">
                <form onSubmit={(e) => void saveName(e)}>
                  <p>
                    <strong>{t('player.email')}:</strong> {me.account.email ?? <span className="muted">{t('player.noEmail')}</span>}
                  </p>
                  <label className="field" htmlFor="player-name">
                    <span>{t('player.name.label')}</span>
                    <input id="player-name" value={name} maxLength={32} autoComplete="nickname" onChange={(e) => setName(e.target.value)} />
                  </label>
                  <p className="muted">{t('player.name.hint')}</p>
                  <button type="submit" className="btn" disabled={busy || name.trim() === (me.account.displayName ?? '')}>
                    {t('player.name.save')}
                  </button>
                </form>
              </section>
            )}

            {tab === 'games' && (
              <section className="card">
                <h2 className="card-title">{t('player.games.title')}</h2>
                {me.games.length === 0 ? (
                  <>
                    <p className="muted">{t('player.games.empty')}</p>
                    <p>{t('player.games.next')}</p>
                  </>
                ) : (
                  <ul className="player-list">
                    {me.games.map((g) => (
                      <li key={g.environmentId} className="player-row">
                        <span>
                          <strong>{g.gameName}</strong> {g.testVersion && <span className="player-tag">{t('player.games.testVersion')}</span>}
                          <br />
                          <span className="muted">{g.lastSignInAt ? t('player.games.lastSignIn', { date: formatAgo(g.lastSignInAt) }) : t('player.games.notYet')}</span>
                        </span>
                        <span className="player-actions">
                          {/* Play は配信中のゲームだけ（playUrl が null = よそで配っている = 開く場所を知らない） */}
                          {g.playUrl && (
                            <a className="btn btn-primary btn-sm" href={g.playUrl} target="_blank" rel="noopener noreferrer">
                              {g.testVersion ? t('player.games.playTest') : t('player.games.play')}
                            </a>
                          )}
                          {/* 隠すのは取り消せる（次のログインでまた出る）= 確認なし。外すは全端末のログアウト = 確認つき */}
                          <button type="button" className="btn btn-ghost btn-sm" disabled={busy} onClick={() => void hide(g)}>
                            {t('player.games.hide')}
                          </button>
                          <button type="button" className="btn btn-ghost btn-sm" disabled={busy} onClick={() => setConfirm({ kind: 'game', game: g })}>
                            {t('player.games.disconnect')}
                          </button>
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
                {me.games.length > 0 && <p className="muted">{t('player.games.hideHint')}</p>}
              </section>
            )}

            {tab === 'sign-in' && (
              <>
                <section className="card">
                  <h2 className="card-title">{t('player.methods.title')}</h2>
                  <p className="muted">{t('player.methods.lead')}</p>
                  <ul className="player-list">
                    {me.methods.map((m) => (
                      <li key={m} className="player-row">
                        <span>{methodName(m)}</span>
                        {/* 最後の 1 つは外せない（サーバーも 409 last_method で断る）= ボタンを出さない */}
                        {me.methods.length > 1 && (
                          <button type="button" className="btn btn-ghost btn-sm" disabled={busy} onClick={() => setConfirm({ kind: 'method', method: m })}>
                            {t('player.methods.remove')}
                          </button>
                        )}
                      </li>
                    ))}
                  </ul>
                  {me.addableMethods.length > 0 && accountPage && (
                    <>
                      <a className="btn btn-sm" href={accountPage} target="_blank" rel="noopener noreferrer">
                        {t('player.methods.add')}
                      </a>
                      <p className="muted">{t('player.methods.addHint')}</p>
                    </>
                  )}
                </section>

                <section className="card">
                  <h2 className="card-title">{t('player.delete.title')}</h2>
                  {me.deletable === false ? (
                    // 作り手でもある人（2026-10-08 core/one-account-3・サーバーの deletable）: ここでは消させない。作り手の中身（お金の台帳の先）は
                    // RESTRICT で、閉鎖の手順（残高の返金・出金の締め = AccountPage）を先に通す。閉じてある人は問い合わせへ
                    me.creator ? (
                      <>
                        <p className="muted">{t('player.delete.creatorLead')}</p>
                        <Link to={CREATOR_ACCOUNT_PATH} className="btn" onClick={() => saveMode(safeLocal(), 'creator')}>
                          {t('player.delete.creatorButton')}
                        </Link>
                      </>
                    ) : (
                      <p className="muted">{t('player.delete.closedLead')}</p>
                    )
                  ) : (
                    <>
                      <p className="muted">{t('player.delete.lead')}</p>
                      <button type="button" className="btn btn-danger" disabled={busy} onClick={() => setConfirm({ kind: 'delete' })}>
                        {t('player.delete.button')}
                      </button>
                    </>
                  )}
                </section>
              </>
            )}
          </>
        )}
      </main>
      <ConfirmDialog
        open={confirm !== null}
        tone="danger"
        busy={busy}
        title={
          confirm?.kind === 'game'
            ? t('player.games.disconnectConfirm', { game: confirm.game.gameName })
            : confirm?.kind === 'method'
              ? t('player.methods.removeConfirm', { method: methodName(confirm.method) })
              : t('player.delete.confirm')
        }
        confirmLabel={confirm?.kind === 'game' ? t('player.games.disconnect') : confirm?.kind === 'method' ? t('player.methods.remove') : t('player.delete.button')}
        onConfirm={() => void runConfirm()}
        onClose={() => setConfirm(null)}
      />
    </div>
  );
}
